<template>
  <div class="login-container clipr-theme">
    <!-- Left Column - Login Form -->
    <div class="login-left">
      <div class="login-content">
        <!-- Logo -->
        <div class="logo-section">
          <img src="/favicon.svg" alt="Logo" class="logo-image" />
        </div>

        <transition name="fade" mode="out-in">
          <!-- 1. Forgot Password Form -->
          <div v-if="showForgotPassword" key="forgot-password">
            <div class="welcome-section">
              <h1 class="welcome-title">{{ $t("login.forgotTitle") }}</h1>
              <p class="welcome-description">
                {{ $t("login.forgotDescription") }}
              </p>
            </div>

            <form @submit.prevent="handleForgotPassword" class="login-form">
              <div class="form-group">
                <label for="reset-email">{{ $t("login.emailLabel") }}</label>
                <div class="input-wrapper">
                  <i class="ti-email input-icon"></i>
                  <input
                    id="reset-email"
                    type="email"
                    v-model="resetPasswordForm.email"
                    :placeholder="$t('login.placeholderEmail')"
                    required
                    class="form-input"
                  />
                </div>
              </div>

              <div v-if="resetError" class="error-message">
                {{ resetError }}
              </div>
              <div v-if="resetSuccess" class="success-message">
                {{ resetSuccess }}
              </div>

              <button
                type="submit"
                class="btn-signin"
                :disabled="resetLoading"
              >
                {{
                  resetLoading
                    ? $t("login.resetSending")
                    : $t("login.resetSendLink")
                }}
              </button>

              <div class="signup-link">
                <a href="#" @click.prevent="switchToLogin">{{
                  $t("login.backToLogin")
                }}</a>
              </div>
            </form>
          </div>

          <!-- 2. Signup Form -->
          <div v-else-if="showSignup" key="signup">
            <div class="welcome-section signup-welcome">
              <h1 class="welcome-title">{{ $t("login.signupTitle") }}</h1>
              <p class="welcome-description">
                {{ $t("login.signupDescription") }}
              </p>
            </div>

            <form @submit.prevent="handleSignup" class="login-form signup-mode">
              <div class="form-group compact-group">
                <label for="full-name">{{ $t("login.fullNameLabel") }}</label>
                <div class="input-wrapper">
                  <i class="ti-user input-icon"></i>
                  <input
                    id="full-name"
                    type="text"
                    v-model="signupForm.fullName"
                    :placeholder="$t('login.namePlaceholder')"
                    required
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-group compact-group">
                <label for="signup-email">{{ $t("login.emailLabel") }}</label>
                <div class="input-wrapper">
                  <i class="ti-email input-icon"></i>
                  <input
                    id="signup-email"
                    type="email"
                    v-model="signupForm.email"
                    :placeholder="$t('login.placeholderEmail')"
                    required
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-group compact-group">
                <label for="signup-password">{{
                  $t("login.passwordLabel")
                }}</label>
                <div class="input-wrapper">
                  <i
                    :class="['input-icon', showSignupPassword ? 'ti-unlock' : 'ti-lock']"
                  ></i>
                  <i
                    class="ti-eye input-icon input-icon-right"
                    @click="showSignupPassword = !showSignupPassword"
                    style="cursor: pointer;"
                  ></i>
                  <input
                    id="signup-password"
                    :type="showSignupPassword ? 'text' : 'password'"
                    v-model="signupForm.password"
                    :placeholder="$t('login.placeholderCreatePassword')"
                    required
                    class="form-input has-right-icon"
                  />
                </div>
                <ul class="password-requirements" v-if="signupForm.password">
                  <li 
                    v-for="(req, index) in passwordRequirements" 
                    :key="index"
                    :class="{ 'met': req.met }"
                  >
                    <i :class="['req-icon', req.met ? 'ti-check' : 'ti-close']"></i>
                    {{ req.label }}
                  </li>
                </ul>
              </div>

              <div class="form-group compact-group">
                <label for="signup-confirm">{{
                  $t("login.confirmPasswordLabel")
                }}</label>
                <div class="input-wrapper">
                  <i
                    :class="['input-icon', showSignupConfirmPassword ? 'ti-unlock' : 'ti-lock']"
                  ></i>
                  <i
                    class="ti-eye input-icon input-icon-right"
                    @click="showSignupConfirmPassword = !showSignupConfirmPassword"
                    style="cursor: pointer;"
                  ></i>
                  <input
                    id="signup-confirm"
                    :type="showSignupConfirmPassword ? 'text' : 'password'"
                    v-model="signupForm.confirmPassword"
                    :placeholder="$t('login.placeholderRepeatPassword')"
                    required
                    class="form-input has-right-icon"
                  />
                </div>
              </div>

              <div v-if="signupError" class="error-message">
                {{ signupError }}
              </div>
              <div v-if="signupSuccess" class="success-message">
                {{ signupSuccess }}
              </div>

              <button
                type="submit"
                class="btn-signin"
                :disabled="signupLoading"
              >
                {{
                  signupLoading
                    ? $t("login.creatingAccount")
                    : $t("login.createAccount")
                }}
              </button>

              <!-- OR Separator -->
              <div class="separator">
                <span class="separator-line"></span>
                <span class="separator-text">{{ $t("login.orSeparator") }}</span>
                <span class="separator-line"></span>
              </div>

              <!-- Social Login Buttons -->
              <div class="social-buttons">
                <div
                  v-if="!googleIdentityFallback"
                  ref="googleSignInSignup"
                  class="google-signin-slot"
                ></div>
                <button
                  v-if="googleIdentityFallback"
                  type="button"
                  class="btn-social"
                  @click="handleGoogleLogin"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H15.9564C17.1591 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
                    <path d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65455 14.4204 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65455 3.57955 9 3.57955Z" fill="#EA4335"/>
                  </svg>
                  {{ $t("login.signUpWithGoogle") }}
                </button>
              </div>

              <div class="signup-link">
                {{ $t("login.alreadyHaveAccount") }}
                <a href="#" @click.prevent="switchToLogin">{{
                  $t("login.signInLink")
                }}</a>
              </div>
            </form>
          </div>

          <!-- 3. Login Form (Default) -->
          <div v-else key="login">
            <div class="welcome-section">
              <h1 class="welcome-title">{{ $t("login.welcomeTitle") }}</h1>
              <p class="welcome-description">
                {{ $t("login.welcomeDescription") }}
              </p>
            </div>

            <form @submit.prevent="handleLogin" class="login-form">
              <div class="form-group">
                <label for="email">{{ $t("login.emailLabel") }}</label>
                <div class="input-wrapper">
                  <i class="ti-email input-icon"></i>
                  <input
                    id="email"
                    type="email"
                    v-model="credentials.email"
                    :placeholder="$t('login.placeholderEmail')"
                    required
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-group">
                <div class="password-label-row">
                  <label for="password">{{ $t("login.passwordLabel") }}</label>
                  <a href="#" class="forgot-password" @click.prevent="startForgotPassword">{{
                    $t("login.forgotPasswordLink")
                  }}</a>
                </div>
                <div class="input-wrapper">
                  <i
                    :class="['input-icon', showLoginPassword ? 'ti-unlock' : 'ti-lock']"
                  ></i>
                  <i
                    class="ti-eye input-icon input-icon-right"
                    @click="showLoginPassword = !showLoginPassword"
                    style="cursor: pointer;"
                  ></i>
                  <input
                    id="password"
                    :type="showLoginPassword ? 'text' : 'password'"
                    v-model="credentials.password"
                    :placeholder="$t('login.placeholderPassword')"
                    required
                    class="form-input has-right-icon"
                  />
                </div>
              </div>

              <div v-if="error" class="error-message">
                {{ error }}
              </div>

              <button
                type="submit"
                class="btn-signin"
                :disabled="loading"
              >
                {{
                  loading ? $t("login.signingIn") : $t("login.signInButton")
                }}
              </button>

              <div class="separator">
                <span class="separator-line"></span>
                <span class="separator-text">{{ $t("login.orSeparator") }}</span>
                <span class="separator-line"></span>
              </div>

              <div class="social-buttons">
                <div
                  v-if="!googleIdentityFallback"
                  ref="googleSignInLogin"
                  class="google-signin-slot"
                ></div>
                <button
                  v-if="googleIdentityFallback"
                  type="button"
                  class="btn-social"
                  @click="handleGoogleLogin"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H15.9564C17.1591 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
                    <path d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65455 14.4204 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65455 3.57955 9 3.57955Z" fill="#EA4335"/>
                  </svg>
                  {{ $t("login.continueWithGoogle") }}
                </button>
              </div>

              <div class="signup-link">
                {{ $t("login.dontHaveAccount") }}
                <a href="#" @click.prevent="startSignup">{{
                  $t("login.signUpLink")
                }}</a>
              </div>
            </form>
          </div>
        </transition>
      </div>
    </div>

  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { initializeLocale } from "@/i18n";
import {
  persistSignupAttribution,
} from "@/api/session";
import {
  getDomainContext,
  getPasswordRecoveryRedirectURL,
  getRedirectURL,
} from "@/utils/auth";
import {
  generateGoogleNonce,
  getCurrentGoogleNonce,
  getGoogleClientId,
  initializeGoogleIdentity,
  promptGoogleOneTap,
  renderGoogleSignInButton,
  shouldUseGoogleIdentity,
} from "@/utils/googleIdentity";

export default {
  name: "Login",
  data() {
    return {
      showSignup: false,
      showForgotPassword: false,
      showLoginPassword: false,
      
      credentials: {
        email: "",
        password: "",
      },
      
      // Signup Data
      signupForm: {
        fullName: "",
        email: "",
        password: "",
        confirmPassword: "",
      },
      showSignupPassword: false,
      showSignupConfirmPassword: false,
      signupLoading: false,
      signupError: null,
      signupSuccess: null,
      
      // Reset Password Data
      resetPasswordForm: {
        email: "",
      },
      resetLoading: false,
      resetError: null,
      resetSuccess: null,

      loading: false,
      error: null,
      googleIdentityFallback: false,
    };
  },
  watch: {
    showSignup() {
      this.$nextTick(() => this.renderVisibleGoogleButton());
    },
    showForgotPassword(value) {
      if (!value) {
        this.$nextTick(() => this.renderVisibleGoogleButton());
      }
    },
  },
  computed: {
    passwordRequirements() {
      void this.$i18n.locale;
      const password = this.signupForm.password;
      const t = (key) =>
        typeof this.$t === "function" ? this.$t(key) : key;
      return [
        { label: t("login.reqMinChars"), met: password.length >= 8 },
        { label: t("login.reqUppercase"), met: /[A-Z]/.test(password) },
        { label: t("login.reqNumber"), met: /[0-9]/.test(password) },
        {
          label: t("login.reqSpecial"),
          met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        },
      ];
    },
    isPasswordValid() {
      return this.passwordRequirements.every(req => req.met);
    }
  },
  methods: {
    startSignup() {
      this.handleGoogleLogin();
    },
    startForgotPassword() {
      this.showForgotPassword = true;
      this.showSignup = false;
      this.error = null;
      this.resetError = null;
      this.resetSuccess = null;
    },
    switchToLogin() {
      this.showSignup = false;
      this.showForgotPassword = false;
      this.signupError = null;
      this.signupSuccess = null;
      this.resetError = null;
      this.resetSuccess = null;
      this.error = null;
    },
    async handleLogin() {
      try {
        this.loading = true;
        this.error = null;
        // Clear stale OAuth redirect data so password login never cross-redirects.
        localStorage.removeItem("oauth_origin");

        const { data, error } = await supabase.auth.signInWithPassword({
          email: this.credentials.email,
          password: this.credentials.password,
        });

        if (error) throw error;

        this.$router.push("/create-clips");
      } catch (error) {
        this.error = error.message || this.$t("login.errorLoginGeneric");
      } finally {
        this.loading = false;
      }
    },
    async handleGoogleCredential(response) {
      try {
        this.loading = true;
        this.error = null;
        localStorage.removeItem("oauth_origin");

        const nonce = getCurrentGoogleNonce();
        const signInPayload = {
          provider: "google",
          token: response.credential,
        };
        if (nonce) signInPayload.nonce = nonce;

        const { error } = await supabase.auth.signInWithIdToken(signInPayload);
        if (error) throw error;

        this.$router.push("/create-clips");
      } catch (error) {
        console.error("Google credential login error:", error);
        this.error = error.message || this.$t("login.errorGoogleLogin");
        await this.handleGoogleLogin();
      } finally {
        this.loading = false;
      }
    },
    async renderVisibleGoogleButton() {
      if (this.googleIdentityFallback || this.showForgotPassword) return;

      const slot = this.showSignup
        ? this.$refs.googleSignInSignup
        : this.$refs.googleSignInLogin;
      if (!slot) return;

      slot.innerHTML = "";
      await renderGoogleSignInButton(slot, {
        width: 340,
      });
    },
    async initializeGoogleAuth() {
      if (!shouldUseGoogleIdentity()) {
        this.googleIdentityFallback = true;
        return;
      }

      const clientId = getGoogleClientId();
      const { hashedNonce } = await generateGoogleNonce();
      const initialized = await initializeGoogleIdentity({
        clientId,
        callback: (response) => this.handleGoogleCredential(response),
        hashedNonce,
      });

      if (!initialized) {
        this.googleIdentityFallback = true;
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        await promptGoogleOneTap();
      }

      await this.$nextTick();
      await this.renderVisibleGoogleButton();
    },
    async handleGoogleLogin() {
      try {
        this.loading = true;
        this.error = null;

        const domainContext = getDomainContext(window.location.hostname);
        // Build OAuth callback URL based on the entry domain (Clipr vs Momo).
        const redirectPath = "/create-clips";
        const redirectTo = getRedirectURL(domainContext, redirectPath);
        const currentOrigin = window.location.origin;
        
        // Save origin + context so callback flow can stay on the same product domain.
        persistSignupAttribution();
        localStorage.setItem("oauth_origin", JSON.stringify({
          origin: currentOrigin,
          context: domainContext,
          flow: "oauth",
          redirectPath,
          timestamp: Date.now(),
        }));
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: redirectTo,
          },
        });

        if (error) throw error;
      } catch (error) {
        console.error("Google login error:", error);
        this.error =
          error.message || this.$t("login.errorGoogleLogin");
        this.loading = false;
      }
    },
    handleSignup() {
      this.handleGoogleLogin();
    },
    async handleForgotPassword() {
      if (!this.resetPasswordForm.email) {
        this.resetError = this.$t("login.resetEmailRequired");
        return;
      }

      try {
        this.resetLoading = true;
        this.resetError = null;
        this.resetSuccess = null;

        const domainContext = getDomainContext(window.location.hostname);
        const redirectTo = getPasswordRecoveryRedirectURL(domainContext);

        const { error } = await supabase.auth.resetPasswordForEmail(this.resetPasswordForm.email, {
          redirectTo,
        });

        if (error) throw error;

        this.resetSuccess = this.$t("login.resetEmailSent");
        this.resetPasswordForm.email = "";
      } catch (error) {
        this.resetError =
          error.message || this.$t("login.resetErrorGeneric");
      } finally {
        this.resetLoading = false;
      }
    },
  },
  async mounted() {
    initializeLocale();

    // Handle OAuth callback errors from URL hash fragments
    // As per Supabase docs, errors are returned as query fragments
    if (window.location.hash) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');
      
      if (errorCode && errorCode.startsWith('4')) {
        // Show error message if error is a 4xx error
        this.error =
          errorDescription || this.$t("login.oauthErrorGeneric");
        // Clean up the URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }

    await this.initializeGoogleAuth();
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  color: var(--clipr-text-primary);
}

/* Estilos para Clipr (app.clipr.tv) - Twitch Users */
.login-container.clipr-theme {
  background: var(--clipr-bg-login-left);
}

.login-container.clipr-theme .login-content {
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.7), 0 0 60px rgba(168, 85, 247, 0.4);
  border-color: var(--clipr-border-purple);
}

/* Estilos para Clipr (clipr-8rxu.onrender.com) - Clipr Users */
.login-container.clipr-theme {
  background: radial-gradient(circle at top, rgba(59, 130, 246, 0.16) 0%, transparent 55%),
    radial-gradient(circle at bottom, rgba(168, 85, 247, 0.16) 0%, transparent 55%),
    var(--clipr-bg-primary);
}

.login-container.clipr-theme .login-content {
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.7), 0 0 60px rgba(59, 130, 246, 0.3);
  border-color: var(--clipr-border-color);
}

/* Left Column - Login Form */
.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.login-content {
  max-width: 460px;
  width: 100%;
  padding: 32px 32px 36px;
  border-radius: var(--clipr-radius-2xl);
  background: var(--clipr-bg-card-glass);
  border: 1px solid var(--clipr-border-color);
  backdrop-filter: blur(24px);
  animation: clipr-float 10s ease-in-out infinite;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

/* Logo Section */
.logo-section {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
}

.logo-image {
  width: 40px;
  height: 40px;
  display: block;
  object-fit: contain;
}

/* Welcome Section */
.welcome-section {
  margin-bottom: 28px;
}

.welcome-title {
  font-size: clamp(28px, 3vw, 32px);
  font-weight: 600;
  color: var(--clipr-text-primary);
  margin: 0 0 10px 0;
  letter-spacing: -0.02em;
}

.welcome-description {
  font-size: 15px;
  color: var(--clipr-text-secondary);
  line-height: 1.4;
  margin: 0;
}

/* Form Styles */
.login-form {
  width: 100%;
}

.login-form.signup-mode .btn-signin {
  margin-bottom: 12px;
}

.form-group {
  margin-bottom: 22px;
}

.compact-group {
  margin-bottom: 12px;
}

.signup-welcome {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.password-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.forgot-password {
  font-size: 12px;
  color: var(--clipr-text-tertiary);
  text-decoration: none;
  font-weight: 500;
}

.forgot-password:hover {
  color: var(--clipr-purple-light);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: var(--clipr-text-tertiary);
  font-size: 18px;
  z-index: 1;
}

.input-icon-right {
  right: 16px;
  left: auto;
}

.form-input {
  width: 100%;
  padding: 12px 16px 12px 46px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-lg);
  font-size: 15px;
  color: var(--clipr-text-primary);
  transition: var(--clipr-transition);
  background-color: rgba(15, 15, 18, 0.9);
}

.form-input.has-right-icon {
  padding-right: 46px;
}

.form-input:focus {
  outline: none;
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 3px var(--clipr-purple-glow);
}

.form-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.error-message {
  background-color: rgba(248, 113, 113, 0.12);
  color: #fecaca;
  border-radius: var(--clipr-radius-md);
  font-size: 13px;
  padding: 10px 12px;
  margin-bottom: 18px;
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.success-message {
  background-color: rgba(34, 197, 94, 0.15);
  color: #bbf7d0;
  border-radius: var(--clipr-radius-md);
  font-size: 13px;
  padding: 10px 12px;
  margin-bottom: 18px;
  border: 1px solid rgba(34, 197, 94, 0.4);
}

/* Sign In / Primary Button */
.btn-signin {
  width: 100%;
  padding: 13px;
  border-radius: var(--clipr-radius-lg);
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 22px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
  transition: var(--clipr-transition);
}

.btn-signin:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 38px rgba(168, 85, 247, 0.35);
}

.btn-signin:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Separator */
.separator {
  display: flex;
  align-items: center;
  margin: 22px 0;
  gap: 14px;
}

.separator-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--clipr-border-color), transparent);
}

.separator-text {
  font-size: 12px;
  color: var(--clipr-text-tertiary);
  font-weight: 500;
  letter-spacing: 0.16em;
}

/* Social Buttons */
.social-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.social-buttons .google-signin-slot {
  align-self: center;
}

.google-signin-slot {
  width: auto;
  min-height: 0;
  display: inline-flex;
  justify-content: flex-start;
  background: transparent;
  overflow: visible;
  line-height: 0;
}

.google-signin-slot > div {
  width: auto !important;
  display: inline-block;
  background: transparent !important;
}

.btn-social {
  width: 100%;
  padding: 11px 16px;
  border-radius: var(--clipr-radius-lg);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid var(--clipr-border-color);
  background: rgba(15, 15, 18, 0.9);
  color: var(--clipr-text-primary);
  transition: var(--clipr-transition);
}

.btn-social:hover {
  border-color: var(--clipr-border-purple);
  background: rgba(26, 26, 31, 0.95);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
}

.btn-social svg {
  flex-shrink: 0;
}

/* Sign Up Link */
.signup-link {
  text-align: center;
  font-size: 13px;
  color: var(--clipr-text-tertiary);
}

.signup-link a {
  color: var(--clipr-purple-light);
  text-decoration: none;
  font-weight: 600;
}

.signup-link a:hover {
  text-decoration: underline;
}

/* Password Validation */
.password-requirements {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 14px;
  row-gap: 8px;
}

.password-requirements li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  color: var(--clipr-text-tertiary);
  transition: color 0.2s;
  line-height: 1.35;
}

.password-requirements li.met {
  color: var(--clipr-text-secondary);
  font-weight: 600;
}

.req-icon {
  font-size: 10px;
  color: #f97373;
}

.password-requirements li.met .req-icon {
  color: #4ade80;
}

/* Responsive Design */
@media (max-width: 968px) {
  .login-container {
    flex-direction: column;
  }

  .login-left {
    padding: 30px 20px;
  }

  .login-content {
    box-shadow: 0 28px 60px rgba(0, 0, 0, 0.7);
  }
}

@media (max-width: 480px) {
  .welcome-title {
    font-size: 26px;
  }

  .login-content {
    padding: 24px 22px 26px;
    border-radius: var(--clipr-radius-xl);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
